"use client";
import * as React from "react";
import { IPlace } from "@/interfaces/property";
import { useTranslation } from "@/lib/i18n";
import { BackgroundImage } from "@/components/ui/image";

interface PlaceCardProps {
  index: number;
  place: IPlace;
  loading?: 'eager' | 'lazy';
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const PlaceCard: React.FC<PlaceCardProps> = ({
  index,
  place,
  loading = 'lazy',
  onClick,
  onMouseEnter,
  onMouseLeave
}) => {
  //console.log("PlaceCard RENDERED", place.location.name, place.location.photos?.[0]?.url);
  //console.log("Provider info:", place.location.provider);
  //console.log("Provider key:", place.location.provider?.key);
  //console.log("Is Google provider:", place.location.provider?.key === "GOOGLE");
  const { t } = useTranslation();

  const imageUrl = place.location.photos?.[0]?.url;
  const hasPhoto = imageUrl && !isLocalLogo(imageUrl);

  function isLocalLogo(url: string) {
    return url && url.includes('placeholder.png');
  }

  // Get category icon based on place type
  const getCategoryIcon = (type?: string) => {
    switch (type) {
      case "food":
        return "/assets/img/categoryIcons/foodCategory.svg";
      case "shopping":
        return "/assets/img/categoryIcons/shoppingCategory.svg";
      case "attraction":
        return "/assets/img/categoryIcons/attractionCategory.svg";
      case "services": 
        return "/assets/img/categoryIcons/servicesCategory.svg";
      case "emergency":
        return "/assets/img/categoryIcons/emergencyCategory.svg";
      default:
        return "/assets/img/categoryIcons/attractionCategory.svg";
    }
  };

  const categoryIcon = getCategoryIcon(place.type);
  const categoryAltText = t(`places_categories.${place.type || 'attraction'}`) || 'Category';

  // Format distance in human-readable way
  const formatDistance = (distance: number) => {
    if (distance < 1000) {
      return `${Math.round(distance)}${t('places.distance_meters')}`;
    } else {
      return `${(distance / 1000).toFixed(1)}${t('places.distance_kilometers')}`;
    }
  };

  // Format distance if available
  const distanceText = place.location.distance?.value !== undefined && place.location.distance?.value !== null
    ? `${formatDistance(place.location.distance.value)} ${t('from_property')}`
    : (place.location.address?.city || place.location.address?.country || t('places.unknown_location'));

  return (
    <div className="flex flex-col cursor-pointer" onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {hasPhoto ? (
        <BackgroundImage
          key={index}
          src={imageUrl}
          fallbackSrc="/assets/img/placeholder.png"
          className="relative flex overflow-hidden rounded-xl bg-buttonGrey
              gap-2 justify-between items-end p-2  
              grow shrink w-full aspect-[177/142]"
          fadeInDuration={400}
          showSkeleton={true}
          loading={loading}
        >
          {/* Category icon */}
          <img
            src={categoryIcon}
            alt={`${categoryAltText}`}
            className="absolute top-0 left-0 object-contain shrink-0 w-[32px] md:w-[36px] aspect-square"
          />

          {/* Google logo - show when provider is Google }
          {(place.location.provider?.key === "GOOGLE" || place.location.provider?.key === "google") && (
            <img
              src="/static/img/logoGoogle.svg"
              alt="Google"
              className="absolute bottom-2 right-2 object-contain shrink-0 w-[18px] md:w-[24px] aspect-square"
            />
          )*/}
        </BackgroundImage>
      ) : (
        <div className="relative overflow-hidden rounded-xl bg-black w-full aspect-[177/142]">
          {/* Category icon */}
          <img
            src={categoryIcon}
            alt={`${categoryAltText}`}
            className="absolute top-1.5 left-1.5 object-contain shrink-0 w-[32px] md:w-[36px] aspect-square z-[1]"
          />

          {/* Google logo - show when provider is Google }
          {(place.location.provider?.key === "GOOGLE" || place.location.provider?.key === "google") && (
            <img
              src="/static/img/logoGoogle.svg"
              alt="Google"
              className="absolute bottom-2 right-2 object-contain shrink-0 w-[18px] md:w-[24px] aspect-square z-10"
            />
          )*/}

          {/* Centered logo */}
          <img
            src="/assets/img/placeholder.png"
            alt="GetLost Logo"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      )}

      {/* Place details */}
      <div className="flex flex-col gap-[4px] md:gap-[6px] mt-[8px] mx-[4px]">
        {/* Place name */}
        <p className="text-foreground text-sm md:text-md font-medium leading-none truncate">
          {place.location.name || t('places.unnamed_place')}
        </p>

        {/* Distance from home or location */}
        <p className="text-tertiary text-sm md:text-md leading-none">{distanceText}</p>
      </div>
    </div>
  );
};

